import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export type LocationType = 'project' | 'share'

export type LocationItem = {
  identifier: string
  name: string
  type: LocationType
}

export type SortDirection = 'newest' | 'oldest'

export type InboxSearchOptions = {
  billing: boolean
  comments: boolean
  replies: boolean
  mentions: boolean
  downloadableAssets: boolean
  starredUpdates: boolean
  location: LocationItem | null
  workspace: string | null
  from: string
  to: string
}

export const defaultInboxSearchOptions: InboxSearchOptions = {
  billing: false,
  comments: false,
  replies: false,
  mentions: false,
  downloadableAssets: false,
  starredUpdates: false,
  location: null,
  workspace: null,
  from: '',
  to: '',
}

interface InboxSearchContextValues {
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  options: InboxSearchOptions
  setOptions: React.Dispatch<React.SetStateAction<InboxSearchOptions>>
  sort: SortDirection
  setSort: React.Dispatch<React.SetStateAction<SortDirection>>
}

const InboxSearchContext = createContext<InboxSearchContextValues | undefined>(
  undefined
)

export const useInboxSearchContext = () => {
  const context = useContext(InboxSearchContext)

  if (typeof context === 'undefined') {
    throw new Error(
      'useInboxSearchContext must be used within an InboxSearchProvider'
    )
  }

  const { search, setSearch, options, setOptions, sort, setSort } = context

  const resetOptions = useCallback(
    () => setOptions(defaultInboxSearchOptions),
    [setOptions]
  )

  const resetAll = useCallback(() => {
    resetOptions()
    setSearch('')
  }, [resetOptions, setSearch])

  const isOptionsEmpty = useMemo(() => {
    const {
      billing,
      comments,
      replies,
      mentions,
      downloadableAssets,
      starredUpdates,
      location,
      workspace,
      from,
      to,
    } = options

    return (
      billing === false &&
      comments === false &&
      replies === false &&
      mentions === false &&
      downloadableAssets === false &&
      starredUpdates === false &&
      location === null &&
      workspace === null &&
      from === '' &&
      to === ''
    )
  }, [options])

  return {
    search,
    setSearch,
    options,
    setOptions,
    sort,
    setSort,
    resetOptions,
    resetAll,
    isOptionsEmpty,
  }
}

export const InboxSearchProvider: React.FCC = ({ children }) => {
  const [search, setSearch] = useState<string>('')
  const [options, setOptions] = useState<InboxSearchOptions>(
    defaultInboxSearchOptions
  )
  const [sort, setSort] = useState<SortDirection>('newest')

  const value = useMemo(
    () => ({ search, setSearch, options, setOptions, sort, setSort }),
    [search, options, sort]
  )

  return (
    <InboxSearchContext.Provider value={value}>
      {children}
    </InboxSearchContext.Provider>
  )
}
